import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";

// Old Vellum-generated suggestions stored goal_field/target_field as human-readable display names
// (e.g. "Mastery Criteria"). New Gemini suggestions store the field_identifier value (e.g.
// "mastery_criteria"). Normalize old → new so suggestion counts work for both.
const LEGACY_FIELD_MAP: Record<string, string> = {
  "Goal Name": "goal_name",
  "Goal Status": "status",
  "Mastery Criteria": "mastery_criteria",
  "Projected Mastery Date": "completion_date",
  "Baseline Data": "baseline_data",
  "Baseline Date": "baseline_date",
  "Projected Introduction Date": "projected_introduction_date",
  "Generalization Criteria": "generalization_criteria",
  "Assessment Tool Source": "assessment_tool_source",
  Rationale: "rationale",
  "Goal Attainment Scale": "goal_attainment_scale",
  Gas_Level1: "gas_level_1",
  Gas_Level3: "gas_level_3",
  Gas_Level4: "gas_level_4",
  "Gas Level1": "gas_level_1",
  "Gas Level3": "gas_level_3",
  "Gas Level4": "gas_level_4",
  "Goal Attainment Scale Score": "gas_score",
  "Progress Summary": "progress_summary",
  "Target Name": "target_name",
};

const normalizeField = (field: string | null | undefined) => (field ? LEGACY_FIELD_MAP[field] ?? field : field);

/** A field id is either a section field id, or "<goalOrTargetId>::<fieldName>" for goal/target fields. */
const splitFieldId = (fieldId: string) => {
  const parts = fieldId.split("::");
  return parts.length >= 2
    ? { ownerId: parts[0], fieldName: parts.slice(1).join("::") }
    : { ownerId: null, fieldName: fieldId };
};

/** Field id an item is anchored to, as the field's own indicator computes it (names not normalized). */
export const anchorOf = (item: ReviewItemFieldsFragment): string | null =>
  item.reportSectionField?.id ||
  (item.reportGoal?.id && item.goalField ? `${item.reportGoal.id}::${item.goalField}` : null) ||
  (item.reportTarget?.id && item.targetField ? `${item.reportTarget.id}::${item.targetField}` : null) ||
  null;

/** Same anchor with legacy goal/target names normalized — only for finding the anchor element on screen. */
export const domAnchorOf = (item: ReviewItemFieldsFragment): string | null =>
  item.reportSectionField?.id ||
  (item.reportGoal?.id && item.goalField ? `${item.reportGoal.id}::${normalizeField(item.goalField)}` : null) ||
  (item.reportTarget?.id && item.targetField ? `${item.reportTarget.id}::${normalizeField(item.targetField)}` : null) ||
  null;

/** Name of the field, goal or target an item is on, as its card and go-to button show it. Blank names count as none. */
export const commentFieldTitle = (item: ReviewItemFieldsFragment): string =>
  item.reportSectionField?.title?.trim() ||
  item.reportGoal?.goalName?.trim() ||
  item.reportTarget?.targetName?.trim() ||
  "";

/** What a card or list says for a field with no name, so it never shows empty quotes. */
export const UNNAMED_FIELD = "this field";

/** A field name for the start of a list row: the name, or the fallback capitalized. */
export const fieldRowLabel = (title: string) => title || UNNAMED_FIELD[0].toUpperCase() + UNNAMED_FIELD.slice(1);

const isAnchoredTo = (item: ReviewItemFieldsFragment, fieldId: string) => {
  const { ownerId, fieldName } = splitFieldId(fieldId);
  if (!ownerId) {
    return item.reportSectionField?.id === fieldId || item.goalField === fieldName || item.targetField === fieldName;
  }
  return (
    (item.reportGoal?.id === ownerId && normalizeField(item.goalField) === fieldName) ||
    (item.reportTarget?.id === ownerId && normalizeField(item.targetField) === fieldName)
  );
};

export const useReviewAnchors = () => {
  const { openQaItems, openComments } = useReviewData();

  const openQaItemsForField = (fieldId: string) =>
    openQaItems.filter((item) => item.reportSectionField?.id === fieldId);

  const countForField = (fieldId: string) => openQaItemsForField(fieldId).length;

  const firstOpenQaItemForField = (fieldId: string) => openQaItemsForField(fieldId)[0];

  const openCommentsForField = (fieldId: string) =>
    openComments.filter((item) => item.reportSectionField?.id === fieldId);

  const commentCountForField = (fieldId: string) => openCommentsForField(fieldId).length;

  const firstOpenCommentForField = (fieldId: string) => openCommentsForField(fieldId)[0];

  const countForGoalOrTarget = (itemId: string, itemType: "stg" | "target", fieldTitle?: string) =>
    openQaItems.filter((item) => {
      const [owner, field] =
        itemType === "stg" ? [item.reportGoal, item.goalField] : [item.reportTarget, item.targetField];
      return owner?.id === itemId && (!fieldTitle || normalizeField(field) === fieldTitle);
    }).length;

  const firstOpenIdForField = (fieldId: string) =>
    [...openQaItems, ...openComments].find((item) => isAnchoredTo(item, fieldId))?.id ?? null;

  return {
    countForField,
    firstOpenQaItemForField,
    commentCountForField,
    firstOpenCommentForField,
    countForGoalOrTarget,
    firstOpenIdForField,
  };
};
